import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Modal, ModalHeader, ModalBody } from "../../../components/Modal";
import CustomButton from "../../../components/CustomButton";
import ScoreBadge from "../../../components/Badges/ScoreBadge";
import { FeedbackInterface, TaskInterface } from "../interfaces";
import { useTranslation } from "react-i18next";

import { RequestFeedbackModal } from "../../../components/Feedback/RequestFeedbackModal";
import EmailIcon from "../../../components/Icons/EmailIcon";
import AiIcon from "../../../components/Icons/AiIcon";
import AccountIcon from "../../../components/Icons/AccountIcon";
import { CalendarWeek as CalendarIcon, Clock, User , EnvelopeOpen as IconEmailOpen, ScaleBalanced as IconScale } from "flowbite-react-icons/outline";
import { getLanguage } from "../../../lang/helpers";
import ScoreTable from "../../../components/ScoreTable";
import { FeedbackQuestionInterface } from "../../../interfaces/common";
import IconCalendarX from "../../../components/Icons/CalendarXIcon";
import InformationCircleIcon from "../../../components/Icons/InformationCircleIcon";
import { storeAiStart } from "../../../helpers/ai_thread";
import { FeedbackInfoModal } from "../../../components/Feedback/FeedbackInfoModal";

interface Props {
  task: TaskInterface;
  feedbacks: Array<FeedbackInterface>;
}

export default function Feedback({ task, feedbacks }: Props) {
  const [feedbackNr, setFeedbackNr] = useState(0);
  const [selectedQuestion, setSelectedQuestion] = useState(0);
  const [scoreInfo, setScoreInfo] = useState(false);
  const [noAnswerInfo, setNoAnswerInfo] = useState(false);
  const [requestFeedbackModalOpen, setRequestFeedbackModalOpen] =
    useState<boolean>(false);
  const [completedFeedbacks, setCompletedFeedbacks] = useState(0);
  const translation = useTranslation();
  const { t, i18n } = translation;
  const lang = getLanguage(i18n.language);
  const navigate = useNavigate();
  const now = new Date();
  const agreeOptions = [
    t("not-answered"),
    t("strongly-disagree"),
    t("disagree"),
    t("neutral"),
    t("agree"),
    t("strongly-agree"),
  ];
  const scoreOptions = [5, 4, 3, 2, 1, 0];

  useEffect(() => {
    let completed = 0;
    for (let feedback of feedbacks) {
      const readyStatus = calculateReadyStatus(feedback);
      if (readyStatus === 0) {
        completed++;
      }
    }
    setCompletedFeedbacks(completed);
  }, []);

  const createAiQuestion = (feedback: FeedbackInterface) => {
    const question = feedback.questions[selectedQuestion];
    let result = t("help-me-analyze-feedback") + ":";
    result += "\nTask: " + task.name;
    result += "\nQuestion: " + question.text;
    const selfAssessment = question.selfAssessmentScore;
    result +=
      "\nMy self-assessment answer was: " + agreeOptions[selfAssessment];

    result += "\nAnswers of feedback givers: ";
    for (let score = 0; score < question.responses.length; score++) {
      const numAnswers = question.responses[score];
      result += `\n${agreeOptions[score]} - [${numAnswers}]`;
    }

    return result;
  };

  const toAiThread = (feedback: FeedbackInterface) => {
    const helpMeAnalyze = createAiQuestion(feedback);
    storeAiStart(
      "coaching",
      "feedbackResult",
      null,
      task.id,
      null,
      helpMeAnalyze
    );
    navigate("/ai/0");
  };

  const renderQuestion = (feedback: FeedbackInterface) => {
    const question = feedback.questions[selectedQuestion];
    const feedbackQuestion: FeedbackQuestionInterface = {
      average: question.responsesScore,
      selfScore: question.selfAssessmentScore,
      gap: question.gap,
      question: question.text,
      responses: [],
    };

    return (
      <>
        {/* Description card */}
        <div className="p-4 bg-indigo-800 rounded-md mt-2">
          <div className="flex flex-wrap">
            {feedback.questions.map((question, index) => {
              let cls =
                "rounded-full border mb-2 mr-2 border-indigo-700 px-3 py-1 " +
                "z-title3 cursor-pointer hover:bg-indigo-900 text-center";
              if (index === selectedQuestion) {
                cls += " bg-indigo-700";
              }

              return (
                <div
                  key={"q_" + index}
                  onClick={() => setSelectedQuestion(index)}
                  className={cls}
                  style={{ minWidth: 92 }}
                >
                  {t("question")} {index + 1}
                </div>
              );
            })}
          </div>

          <hr className="border-indigo-700 mt-2" />

          <p className="text-gray-400 mt-2">
            {feedback.questions[selectedQuestion].text}
          </p>

          {feedbackQuestion.selfScore > 0.0 ? (
            <>
              <div className="z-title1 text-white mt-4">
                {lang === "sv" ? "Frågans resultat" : "Question result"}
              </div>
              <ScoreTable
                question={feedbackQuestion}
                type="task"
                size="small"
                circleNumbers={false}
              />
            </>
          ) : (
            // <h2 className="z-title1 text-white mt-4">
            //   {lang === 'sv' ? 'Frågans resultat' : 'Question result'}: {feedbackQuestion.average.toFixed(1)}
            // </h2>
            <></>
          )}

          <h2 className="z-title1 text-white mt-4">
            {t("answers")}
          </h2>

          {/* Answers horizontal bar chart. Reverse the array, since we show highest value first */}
          <div className="w-full flex flex-col gap-4 mt-4">
            {scoreOptions.map((position, idx) => {
              const score = 5 - idx;
              const responsesNr = question.responses[score];
              const option = agreeOptions[position];
              // Calculate percentage for this answer
              const percentage =
                responsesNr > 0 ? (responsesNr / feedback.numAnswers) * 100 : 0;

              return (
                <div key={`answer-${idx}`} className="">
                  {/* Label and Value Section */}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <div className="z-title3 text-gray-400">
                        {option}
                      </div>
                      {idx === 5 && (
                        <div className="ml-2">
                          <CustomButton
                            buttonType="transparentBase"
                            size="sm"
                            isRound
                            leftIcon={<InformationCircleIcon alt="info" />}
                            onClick={() => setNoAnswerInfo(true)}
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex items-center">
                      {position === question.selfAssessmentScore && (
                        <div className="mr-2">
                          <ScoreBadge
                            txt={t("you")}
                            type="task"
                            size="sm"
                            self={true}
                          />
                        </div>
                      )}
                      <User className="w-4 h-4" aria-hidden="true"/>
                      <span className="ml-2 text-white font-medium">
                        {responsesNr}
                      </span>
                    </div>
                  </div>
                  {/* Bar Section */}
                  <div className="w-full bg-indigo-700 rounded-full h-5 mt-1">
                    <div
                      className={"bg-royal-400 h-5 rounded-full"}
                      style={{ width: `${percentage}%` }}
                    >
                      {percentage > 0 && (
                        <div className="flex justify-end items-center text-black z-title4 pr-1">
                          {percentage.toFixed(0)}%
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <CustomButton
            className="mt-6"
            buttonType="outlineWhiteBase"
            leftIcon={<AiIcon alt="ai" />}
            onClick={() => toAiThread(feedback)}
          >
            {t("discuss-this-result")}
          </CustomButton>
        </div>
      </>
    );
  };

  const renderFeedbackData = (feedback: FeedbackInterface) => {
    return (
      <>
        {/*{*/}
        {/*  feedback.selfAssessmentScore > 0.0 &&*/}
        {/*    <>*/}
        {/*      <div className="flex items-center mt-4">*/}
        {/*        <div className="text-lg font-medium">{t('result-overview')}</div>*/}
        {/*        <img src={iconInfo} alt="info" className="ml-2 w-6 h-6 cursor-pointer"*/}
        {/*             onClick={() => setScoreInfo(true)}/>*/}
        {/*      </div>*/}
        {/*      <div className="bg-neutral-800 mt-2 p-4 rounded-[6px]">*/}
        {/*        <Infographic*/}
        {/*            scoreSelf={feedback.selfAssessmentScore}*/}
        {/*            scoreRespondents={feedback.responsesScore}*/}
        {/*            lastFeedbackDate={new Date(feedback.lastFeedback)}*/}
        {/*            type="task"*/}
        {/*        />*/}
        {/*      </div>*/}
        {/*    </>*/}
        {/*}*/}

        <div className="mt-6 z-title1">
          {lang === "sv" ? "Resultat" : "Result"}
        </div>

        {renderQuestion(feedback)}
        <Modal isOpen={noAnswerInfo} onClose={() => setNoAnswerInfo(false)}>
          <ModalHeader onClose={() => setNoAnswerInfo(false)}>
            <div className="z-title1">{t("why-no-answer")}</div>
          </ModalHeader>
          <ModalBody>
            <div className="font-medium">{t("why-no-answer-1")}</div>
            <div className="mt-1">{t("why-no-answer-2")}</div>

            <div className="font-medium mt-4">{t("why-no-answer-3")}</div>
            <div className="mt-1">{t("why-no-answer-4")}</div>

            <div className="font-medium mt-4">{t("why-no-answer-5")}</div>
            <div className="mt-1">{t("why-no-answer-6")}</div>
          </ModalBody>
        </Modal>
      </>
    );
  };

  const calculateReadyStatus = (feedback: FeedbackInterface) => {
    let deadline = new Date(feedback.deadline);
    let tooFewRespondents = feedback.numAnswers < 1;
    let deadlinePassed = deadline < now;

    if (deadlinePassed && tooFewRespondents) {
      return 1;
    } else if (!deadlinePassed && tooFewRespondents) {
      return 3;
    }
    return 0;
  };

  const renderFeedback = (feedback: FeedbackInterface) => {
    const readyStatus = calculateReadyStatus(feedback);

    return (
      <>
        {readyStatus === 0 && renderFeedbackData(feedback)}
        {readyStatus === 1 && (
          <div className="border border-gray-500 rounded-md mt-4 p-4 bg-indigo-800 flex flex-col items-start gap-2">
            <div className="flex items-center gap-2">
              <IconCalendarX className="h-6 w-6 text-neutral-400" aria-hidden="true" />
              <div className="z-title2">
                {t("not-enough-responses")}
              </div>
            </div>

            <div className="z-body2 text-gray-200 ">
              {t("previous-feedback-failed-message")}
            </div>

            {feedbackNr === 0 && (
              <CustomButton
                className="mt-0"
                onClick={() => setRequestFeedbackModalOpen(true)}
              >
                {t("request-new-feedback")}
              </CustomButton>
            )}
          </div>
        )}
        {readyStatus > 1 && (
          <div className="border border-gray-500 rounded-md mt-4 p-4 bg-indigo-800 flex flex-col items-start gap-2">
            <div className="flex items-center gap-2">
              <Clock className="h-6"/>
              <div className="font-medium">
                {lang === "sv"
                  ? "Väntar på resultat"
                  : "Result pending"}
              </div>
            </div>
            <div className="text-sm">
              {lang === "sv"
                ? "Förfrågan pågår. Vi skickar ett e-postmeddelande när det första svaret kommer in – och uppdaterar resultatet med ny feedback fram till tidsfristen."
                : "Request pending. We’ll email you when the first reply comes in - and keep updating the result with new feedback until the deadline."}
            </div>
          </div>
        )}
        <div className="mt-6">
          <div className="z-title1">{t("details")}</div>
          <div className="flex items-center mt-4 gap-x-1">
            <EmailIcon className="w-4 h-4" alt="sent"/>
            <div className="z-title3">{t("request-sent")}:</div>
            <div className="text-sm text-gray-400">
              {feedback.created.substring(0, 10)}
            </div>
          </div>

          <hr className="mt-2 border-indigo-800" />

          <div className="flex items-center mt-2 gap-x-1">
            <CalendarIcon className="w-4 h-4" aria-hidden="true" />
            <div className="z-title3">{t("result-created-at")}:</div>
            <div className="text-sm text-gray-400">
              {readyStatus === 0
                ? feedback.lastFeedback.substring(0, 10)
                : readyStatus > 1
                ? "Pending"
                : t("no-result")}
            </div>
          </div>

          {readyStatus < 2 && (
            <>
              <hr className="mt-2 border-indigo-800" />
              <div className="flex items-center mt-2 gap-x-1">
                <IconEmailOpen className="w-4 h-4 text-neutral-400" aria-hidden="true" />
                <div className="z-title3">{t("received")}:</div>
                <div className="text-sm text-gray-400">
                  {feedback.numAnswers} {lang === "sv" ? "av" : "out of"}{" "}
                  {feedback.totalFeedbackUsers}
                </div>
              </div>
            </>
          )}

          <hr className="mt-2 border-indigo-800" />

          <div className="flex mt-2 gap-x-1">
            <AccountIcon className="w-4 h-4 mt-1" alt="sent" />
            <div>
              <span className="z-title3">
                {t("requested-from")}:
              </span>
              <span className="text-sm text-gray-400 m-2">
                {feedback.feedbackUsers.map((email, i) => (
                  <span key={"r_" + email}>
                    {i > 0 ? ", " : ""}
                    {email}
                  </span>
                ))}
              </span>
            </div>
          </div>
        </div>

        {feedbackNr === 0 && completedFeedbacks === 0 && readyStatus !== 3 && (
          <>
            <div className="mt-6 z-title1">
              {lang === "sv"
                ? "Skicka en ny feedback-förfrågan"
                : "Send a new feedback request"}
            </div>
            <CustomButton
              className="mt-2"
              buttonType="outlineWhiteBase"
              onClick={() => setRequestFeedbackModalOpen(true)}
            >
              {t("request-feedback")}
            </CustomButton>
          </>
        )}
      </>
    );
  };

  if (feedbacks.length === 0) {
    return (
      <div className="mt-4">
        {task.completed ? (
          <>
            <IconScale className="w-6 h-6 mx-auto text-neutral-400" aria-hidden="true" />
            <div className="mt-2 text-gray-400 text-center">
              {lang === "sv"
                ? "Att jämföra din självskattning med kollegors feedback skapar en grund för utveckling och möjliggör mätning av framsteg."
                : "Comparing your self-assessment with colleague feedback provides a baseline for improvement and ensures progress can be effectively tracked."}
            </div>
            <CustomButton
              className="mt-4"
              buttonType="blueBase"
              onClick={() => setRequestFeedbackModalOpen(true)}
            >
              {t("request-feedback")}
            </CustomButton>
            {requestFeedbackModalOpen && (
              <RequestFeedbackModal
                lang={lang}
                t={t}
                nrFeedbacks={feedbacks.length}
                modalOpen={requestFeedbackModalOpen}
                setModalOpen={setRequestFeedbackModalOpen}
                type="task"
                originId={task.id}
              />
            )}
          </>
        ) : (
          <>
            <IconScale
              aria-hidden="true"
              className="w-6 h-6 mt-6 mx-auto text-neutral-400"
            />
            <div className="mt-2 text-gray-400 text-center">
              {lang === "sv"
                ? "Inget feedbackresultat ännu."
                : "No feedback result yet."}
            </div>
            <div className="mt-1 text-gray-400 text-center">
              {lang === "sv"
                ? "Markera denna Åtgärd som slutförd först för att be om feedback."
                : "Mark this Action as completed first to request feedback."}
            </div>
          </>
        )}
      </div>
    );
  }

  const feedback = feedbacks[feedbackNr];

  return (
    <>
      {/*{*/}
      {/*  feedbacks.length > 1 &&*/}
      {/*    <select className="z-select" value={feedbackNr}*/}
      {/*            onChange={(e) => setFeedbackNr(parseInt(e.target.value))}>*/}
      {/*      {*/}
      {/*        feedbacks.map((feedback, i) => {*/}
      {/*          let optionName = feedback.created.substring(0, 10)*/}
      {/*          if (i === 0) {*/}
      {/*            const readyStatus = calculateReadyStatus(feedback)*/}
      {/*            if (readyStatus > 1) {*/}
      {/*              optionName = lang === 'sv' ? 'Väntar på svar' : 'Pending result'*/}
      {/*            }*/}
      {/*          }*/}
      {/*          return (*/}
      {/*            <option value={i} key={"feedback_nr_" + i}>{optionName}</option>*/}
      {/*          )*/}
      {/*        })*/}
      {/*      }*/}
      {/*    </select>*/}
      {/*}*/}

      {renderFeedback(feedback)}

      {scoreInfo && (
        <FeedbackInfoModal
          modalOpen={scoreInfo}
          setModalOpen={setScoreInfo}
          t={t}
          gap={0}
          selfScore={0}
          responsesScore={0}
          type="task"
        />
      )}
      {requestFeedbackModalOpen && (
        <RequestFeedbackModal
          lang={lang}
          t={t}
          nrFeedbacks={feedbacks.length}
          modalOpen={requestFeedbackModalOpen}
          setModalOpen={setRequestFeedbackModalOpen}
          type="task"
          originId={task.id}
        />
      )}
    </>
  );
}
